import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ShieldCheck, ListChecks, Gavel, AlertOctagon, Trash2, CheckCircle2, Clock4, XCircle, Pause } from "lucide-react";
import { toast } from "sonner";

const GATE_STAGES = [
  { key: "g0_concept", label: "G0 Concept" },
  { key: "g1_initiation", label: "G1 Initiation" },
  { key: "g2_planning", label: "G2 Planning" },
  { key: "g3_execution", label: "G3 Execution" },
  { key: "g4_uat", label: "G4 UAT" },
  { key: "g5_closure", label: "G5 Closure" },
];

const GATE_STATUS = [
  { key: "pending",   label: "Pending",   icon: Clock4,       color: "bg-[#1F2937] text-[#6B7280] border-[#374151]" },
  { key: "in_review", label: "In review", icon: Clock4,       color: "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/30" },
  { key: "approved",  label: "Approved",  icon: CheckCircle2, color: "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/50" },
  { key: "rejected",  label: "Rejected",  icon: XCircle,      color: "bg-[#FF007A]/10 text-[#FF007A] border-[#FF007A]/30" },
  { key: "waived",    label: "Waived",    icon: Pause,        color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
];

const POLICY_CATEGORIES = ["general", "financial", "quality", "security", "delivery"];
const CHECK_CATEGORIES = ["GDPR", "Security", "Finance", "Legal", "Quality", "Delivery"];

const tabs = [
  { key: "scorecard",   label: "Scorecard",    icon: ShieldCheck },
  { key: "gates",       label: "Gate Reviews", icon: Gavel },
  { key: "compliance",  label: "Compliance",   icon: ListChecks },
  { key: "policies",    label: "Policies",     icon: AlertOctagon },
];

const Governance = () => {
  const [tab, setTab] = useState("scorecard");
  const [projects, setProjects] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [gates, setGates] = useState([]);
  const [checks, setChecks] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [filterProject, setFilterProject] = useState("__all__");

  const [gateOpen, setGateOpen] = useState(false);
  const [gateForm, setGateForm] = useState({ project_id: "", stage: "g1_initiation", title: "", description: "", due_date: "", status: "pending" });

  const [checkOpen, setCheckOpen] = useState(false);
  const [checkForm, setCheckForm] = useState({ project_id: "", category: "GDPR", item: "", status: "pending", notes: "" });

  const [polOpen, setPolOpen] = useState(false);
  const [polForm, setPolForm] = useState({ name: "", description: "", category: "general", rule_text: "", active: true });

  const load = useCallback(async () => {
    const [p, s, g, c, pol] = await Promise.all([
      api.get("/projects"),
      api.get("/governance/scorecard"),
      api.get("/governance/gates"),
      api.get("/governance/checklists"),
      api.get("/governance/policies"),
    ]);
    setProjects(p.data);
    setScorecard(s.data);
    setGates(g.data);
    setChecks(c.data);
    setPolicies(pol.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const projNameMap = Object.fromEntries(projects.map((p) => [p.project_id, p.name]));

  const createGate = async () => {
    if (!gateForm.project_id || !gateForm.title.trim()) {
      toast.error("Project and title required");
      return;
    }
    try {
      await api.post("/governance/gates", { ...gateForm, due_date: gateForm.due_date || null });
      setGateOpen(false);
      setGateForm({ project_id: "", stage: "g1_initiation", title: "", description: "", due_date: "", status: "pending" });
      toast.success("Gate created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const updateGateStatus = async (gid, status) => {
    setGates((gs) => gs.map((g) => (g.gate_id === gid ? { ...g, status } : g)));
    await api.patch(`/governance/gates/${gid}`, { status });
    load();
  };

  const deleteGate = async (gid) => {
    if (!window.confirm("Delete this gate?")) return;
    await api.delete(`/governance/gates/${gid}`);
    toast.success("Gate removed");
    load();
  };

  const createCheck = async () => {
    if (!checkForm.project_id || !checkForm.item.trim()) {
      toast.error("Project and item required");
      return;
    }
    try {
      await api.post("/governance/checklists", checkForm);
      setCheckOpen(false);
      setCheckForm({ project_id: "", category: "GDPR", item: "", status: "pending", notes: "" });
      toast.success("Compliance item added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const updateCheckStatus = async (cid, status) => {
    setChecks((cs) => cs.map((c) => (c.checklist_id === cid ? { ...c, status } : c)));
    await api.patch(`/governance/checklists/${cid}`, { status });
    load();
  };

  const deleteCheck = async (cid) => {
    await api.delete(`/governance/checklists/${cid}`);
    load();
  };

  const createPolicy = async () => {
    if (!polForm.name.trim()) return;
    try {
      await api.post("/governance/policies", polForm);
      setPolOpen(false);
      setPolForm({ name: "", description: "", category: "general", rule_text: "", active: true });
      toast.success("Policy created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const togglePolicy = async (pid, active) => {
    await api.patch(`/governance/policies/${pid}`, { active });
    load();
  };

  const deletePolicy = async (pid) => {
    if (!window.confirm("Delete policy?")) return;
    await api.delete(`/governance/policies/${pid}`);
    load();
  };

  const filteredGates = filterProject === "__all__" ? gates : gates.filter((g) => g.project_id === filterProject);
  const filteredChecks = filterProject === "__all__" ? checks : checks.filter((c) => c.project_id === filterProject);

  const dialogCls = "rounded-none bg-[#111827] border-[#374151] text-white";
  const inputCls = "rounded-none bg-[#030712] border-[#374151] text-white placeholder:text-[#4B5563] focus:ring-[#00F5D4]";
  const labelCls = "text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]";
  const btnPrimary = "rounded-none bg-[#00F5D4] hover:bg-[#00D4B8] text-[#030712] font-semibold";

  return (
    <DarkPage>
      <PageHeader
        eyebrow="PMO Governance Centre"
        title="Stage gates, compliance & policy"
        description="Run a tight PMO: enforce gate reviews, track compliance, and publish the rules of the road."
      />

      {/* Tabs */}
      <div className="border-b border-[#1F2937] px-5 sm:px-8 lg:px-12 bg-[#030712] sticky top-0 z-10">
        <div className="flex gap-6 overflow-x-auto">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`py-3 text-[11px] uppercase tracking-[0.25em] border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
                tab === key ? "border-[#00F5D4] text-white" : "border-transparent text-[#6B7280] hover:text-white"
              }`}
              data-testid={`gov-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-6">
        {/* SCORECARD TAB */}
        {tab === "scorecard" && scorecard && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2937] border border-[#1F2937]">
              {[
                ["Projects governed", scorecard.total_projects, "text-white"],
                ["Compliance rate", `${scorecard.compliance_rate}%`, "text-[#00F5D4]"],
                ["Overdue gates", scorecard.overdue_gates, scorecard.overdue_gates > 0 ? "text-[#FF007A]" : "text-[#00F5D4]"],
                ["Active policies", scorecard.active_policies, "text-white"],
              ].map(([l, v, col]) => (
                <div key={l} className="bg-[#111827] p-6">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">{l}</div>
                  <div className={`font-display font-black text-4xl tracking-tighter mt-2 ${col}`} data-testid={`scorecard-${l.replace(/\s+/g, "-").toLowerCase()}`}>{v}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1F2937] border border-[#1F2937]">
              <div className="bg-[#111827] p-6">
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Gate review status</div>
                <div className="space-y-2">
                  {GATE_STATUS.map((s) => {
                    const count = scorecard.gate_status[s.key] || 0;
                    const total = Object.values(scorecard.gate_status).reduce((a, b) => a + b, 0) || 1;
                    const pct = Math.round((count / total) * 100);
                    const barColor = s.color.split(" ")[0];
                    return (
                      <div key={s.key} className="flex items-center gap-3">
                        <div className="w-24 text-xs text-[#9CA3AF]">{s.label}</div>
                        <div className="flex-1 h-5 bg-[#1F2937] relative">
                          <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-12 text-right font-mono text-xs text-[#9CA3AF]">{count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-[#111827] p-6">
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Compliance status</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { k: "done",    label: "Done",    color: "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/30" },
                    { k: "pending", label: "Pending", color: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
                    { k: "n_a",     label: "N/A",     color: "bg-[#1F2937] text-[#6B7280] border-[#374151]" },
                  ].map(({ k, label, color }) => (
                    <div key={k} className={`border ${color} p-4`}>
                      <div className="text-[10px] uppercase tracking-[0.2em]">{label}</div>
                      <div className="font-display font-black text-3xl tracking-tighter mt-1">{scorecard.checklist_status[k] || 0}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="border border-[#1F2937]">
              <div className="px-6 py-3 border-b border-[#1F2937] font-display font-bold tracking-tight text-sm">Per-project governance health</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#1F2937] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
                    <tr>
                      <th className="text-left px-4 py-2">Project</th>
                      <th className="text-right px-4 py-2">Gates approved</th>
                      <th className="text-right px-4 py-2">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scorecard.projects.map((p) => (
                      <tr key={p.project_id} className="border-t border-[#1F2937]" data-testid={`gov-row-${p.project_id}`}>
                        <td className="px-4 py-2">{p.name}</td>
                        <td className="px-4 py-2 text-right font-mono">{p.gates_approved}/{p.gates_total}</td>
                        <td className="px-4 py-2 text-right font-mono">
                          <span className={p.compliance_pct >= 80 ? "text-[#00F5D4]" : p.compliance_pct >= 50 ? "text-[#F59E0B]" : "text-[#FF007A]"}>
                            {p.compliance_pct}%
                          </span>
                          <span className="text-[#4B5563] text-[10px] ml-1">({p.checks_done}/{p.checks_total})</span>
                        </td>
                      </tr>
                    ))}
                    {scorecard.projects.length === 0 && <tr><td colSpan="3" className="px-4 py-6 text-center text-[#6B7280] text-xs">No projects.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* GATES TAB */}
        {tab === "gates" && (
          <>
            <div className="flex items-center justify-between">
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="w-64 rounded-none bg-[#030712] border-[#374151] text-white" data-testid="filter-project-select"><SelectValue placeholder="All projects" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#374151] text-white">
                  <SelectItem value="__all__">All projects</SelectItem>
                  {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Dialog open={gateOpen} onOpenChange={setGateOpen}>
                <DialogTrigger asChild>
                  <Button className={btnPrimary} data-testid="new-gate-btn">
                    <Plus className="w-4 h-4 mr-1" /> New gate review
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${dialogCls} sm:max-w-lg`}>
                  <DialogHeader><DialogTitle className="font-display tracking-tight text-white">New gate review</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className={labelCls}>Project</Label>
                      <Select value={gateForm.project_id} onValueChange={(v) => setGateForm({ ...gateForm, project_id: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="gate-project-select"><SelectValue placeholder="Pick project" /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">{projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className={labelCls}>Stage</Label>
                        <Select value={gateForm.stage} onValueChange={(v) => setGateForm({ ...gateForm, stage: v })}>
                          <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="gate-stage-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#111827] border-[#374151] text-white">{GATE_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className={labelCls}>Due date</Label>
                        <Input type="date" className={`${inputCls} mt-1`} data-testid="gate-due-input" value={gateForm.due_date} onChange={(e) => setGateForm({ ...gateForm, due_date: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <Label className={labelCls}>Title</Label>
                      <Input className={`${inputCls} mt-1`} placeholder="e.g. Steering committee sign-off" data-testid="gate-title-input" value={gateForm.title} onChange={(e) => setGateForm({ ...gateForm, title: e.target.value })} />
                    </div>
                    <div>
                      <Label className={labelCls}>Description</Label>
                      <Textarea className={`${inputCls} mt-1`} data-testid="gate-desc-input" value={gateForm.description} onChange={(e) => setGateForm({ ...gateForm, description: e.target.value })} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createGate} className={btnPrimary} data-testid="confirm-create-gate">Create</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-[#1F2937]" data-testid="gates-list">
              {filteredGates.length === 0 ? (
                <div className="p-10 text-center text-sm text-[#6B7280]">No gate reviews yet.</div>
              ) : (
                <div className="divide-y divide-[#1F2937]">
                  {filteredGates.map((g) => {
                    const status = GATE_STATUS.find((s) => s.key === g.status) || GATE_STATUS[0];
                    const stage = GATE_STAGES.find((s) => s.key === g.stage)?.label || g.stage;
                    return (
                      <div key={g.gate_id} className="p-4 hover:bg-[#111827] transition-colors" data-testid={`gate-row-${g.gate_id}`}>
                        <div className="flex items-start gap-4">
                          <div className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] border ${status.color}`}>
                            {stage}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{g.title}</div>
                            <div className="text-xs text-[#6B7280] mt-0.5">{projNameMap[g.project_id]} {g.due_date && <span className="font-mono ml-2">▸ due {new Date(g.due_date).toLocaleDateString()}</span>}</div>
                            {g.description && <div className="text-xs text-[#9CA3AF] mt-1">{g.description}</div>}
                          </div>
                          <Select value={g.status} onValueChange={(v) => updateGateStatus(g.gate_id, v)}>
                            <SelectTrigger className={`rounded-none w-36 h-8 text-xs ${status.color}`} data-testid={`gate-status-${g.gate_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#111827] border-[#374151] text-white">{GATE_STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                          <button onClick={() => deleteGate(g.gate_id)} className="text-[#374151] hover:text-[#FF007A]" data-testid={`delete-gate-${g.gate_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* CHECKLISTS TAB */}
        {tab === "compliance" && (
          <>
            <div className="flex items-center justify-between">
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="w-64 rounded-none bg-[#030712] border-[#374151] text-white"><SelectValue placeholder="All projects" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#374151] text-white">
                  <SelectItem value="__all__">All projects</SelectItem>
                  {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Dialog open={checkOpen} onOpenChange={setCheckOpen}>
                <DialogTrigger asChild>
                  <Button className={btnPrimary} data-testid="new-check-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add compliance item
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${dialogCls} sm:max-w-lg`}>
                  <DialogHeader><DialogTitle className="font-display tracking-tight text-white">Add compliance item</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className={labelCls}>Project</Label>
                      <Select value={checkForm.project_id} onValueChange={(v) => setCheckForm({ ...checkForm, project_id: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="check-project-select"><SelectValue placeholder="Pick project" /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">{projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelCls}>Category</Label>
                      <Select value={checkForm.category} onValueChange={(v) => setCheckForm({ ...checkForm, category: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="check-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">{CHECK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelCls}>Item</Label>
                      <Input className={`${inputCls} mt-1`} placeholder="e.g. DPIA signed off by DPO" data-testid="check-item-input" value={checkForm.item} onChange={(e) => setCheckForm({ ...checkForm, item: e.target.value })} />
                    </div>
                    <div>
                      <Label className={labelCls}>Notes</Label>
                      <Textarea className={`${inputCls} mt-1`} data-testid="check-notes-input" value={checkForm.notes} onChange={(e) => setCheckForm({ ...checkForm, notes: e.target.value })} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createCheck} className={btnPrimary} data-testid="confirm-create-check">Add</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-[#1F2937]" data-testid="checks-list">
              {filteredChecks.length === 0 ? (
                <div className="p-10 text-center text-sm text-[#6B7280]">No compliance items yet.</div>
              ) : (
                <div className="divide-y divide-[#1F2937]">
                  {filteredChecks.map((c) => (
                    <div key={c.checklist_id} className="p-4 flex items-center gap-3 hover:bg-[#111827] transition-colors" data-testid={`check-row-${c.checklist_id}`}>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-[0.15em] bg-[#1F2937] border border-[#374151] text-[#6B7280]">{c.category}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm ${c.status === "done" ? "line-through text-[#4B5563]" : "text-white"}`}>{c.item}</div>
                        <div className="text-[11px] text-[#6B7280] mt-0.5">{projNameMap[c.project_id]}</div>
                        {c.notes && <div className="text-xs text-[#9CA3AF] mt-1">{c.notes}</div>}
                      </div>
                      <Select value={c.status} onValueChange={(v) => updateCheckStatus(c.checklist_id, v)}>
                        <SelectTrigger className="rounded-none w-32 h-8 text-xs bg-[#030712] border-[#374151] text-white" data-testid={`check-status-${c.checklist_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="done">Done</SelectItem>
                          <SelectItem value="n_a">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => deleteCheck(c.checklist_id)} className="text-[#374151] hover:text-[#FF007A]" data-testid={`delete-check-${c.checklist_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* POLICIES TAB */}
        {tab === "policies" && (
          <>
            <div className="flex items-center justify-end">
              <Dialog open={polOpen} onOpenChange={setPolOpen}>
                <DialogTrigger asChild>
                  <Button className={btnPrimary} data-testid="new-policy-btn">
                    <Plus className="w-4 h-4 mr-1" /> New policy
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${dialogCls} sm:max-w-lg`}>
                  <DialogHeader><DialogTitle className="font-display tracking-tight text-white">New PMO policy</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className={labelCls}>Name</Label>
                      <Input className={`${inputCls} mt-1`} placeholder="e.g. Projects > £50k need steering" data-testid="policy-name-input" value={polForm.name} onChange={(e) => setPolForm({ ...polForm, name: e.target.value })} />
                    </div>
                    <div>
                      <Label className={labelCls}>Category</Label>
                      <Select value={polForm.category} onValueChange={(v) => setPolForm({ ...polForm, category: v })}>
                        <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="policy-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#111827] border-[#374151] text-white">{POLICY_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={labelCls}>Short description</Label>
                      <Input className={`${inputCls} mt-1`} data-testid="policy-desc-input" value={polForm.description} onChange={(e) => setPolForm({ ...polForm, description: e.target.value })} />
                    </div>
                    <div>
                      <Label className={labelCls}>Rule text</Label>
                      <Textarea rows={4} className={`${inputCls} mt-1`} placeholder="The detailed policy / rule…" data-testid="policy-rule-input" value={polForm.rule_text} onChange={(e) => setPolForm({ ...polForm, rule_text: e.target.value })} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createPolicy} className={btnPrimary} data-testid="confirm-create-policy">Publish</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2937] border border-[#1F2937]" data-testid="policies-list">
              {policies.length === 0 && <div className="bg-[#111827] p-10 text-center text-sm text-[#6B7280] md:col-span-2">No policies yet. Publish your first rule of the road.</div>}
              {policies.map((p) => (
                <div key={p.policy_id} className="bg-[#111827] p-5" data-testid={`policy-card-${p.policy_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.2em] bg-[#1F2937] text-[#6B7280] px-1.5 py-0.5 border border-[#374151]">{p.category}</span>
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5 border ${p.active ? "bg-[#00F5D4]/10 text-[#00F5D4] border-[#00F5D4]/30" : "bg-[#1F2937] text-[#6B7280] border-[#374151]"}`}>
                        {p.active ? "Active" : "Draft"}
                      </span>
                    </div>
                    <button onClick={() => deletePolicy(p.policy_id)} className="text-[#374151] hover:text-[#FF007A]" data-testid={`delete-policy-${p.policy_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="font-display font-bold tracking-tight mt-3">{p.name}</div>
                  {p.description && <div className="text-xs text-[#9CA3AF] mt-1">{p.description}</div>}
                  {p.rule_text && <div className="text-xs text-[#9CA3AF] mt-3 whitespace-pre-wrap bg-[#030712] p-3 border border-[#374151]">{p.rule_text}</div>}
                  <button onClick={() => togglePolicy(p.policy_id, !p.active)} className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#00F5D4] hover:underline" data-testid={`toggle-policy-${p.policy_id}`}>
                    {p.active ? "Mark as draft" : "Activate"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DarkPage>
  );
};

export default Governance;
